import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useSavedView } from '../hooks/useSavedView';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';
import type { AuditLogRow } from '../../../shared/api';

interface AuditFilters {
  action: string;
  entityType: string;
  from: string;
  to: string;
}

export function AuditLog() {
  const [filters, setFilters] = useSavedView<AuditFilters>('audit-filters', { action: '', entityType: '', from: '', to: '' });
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v).map(([k, v]) => [k, v]),
  );
  qs.set('limit', '200');

  const { data, isLoading } = useQuery({
    queryKey: ['audit', filters],
    queryFn: () => api<{ data: AuditLogRow[] }>(`/api/audit/logs?${qs.toString()}`),
  });

  return (
    <div>
      <div className="mb-6">
        <p className="qa-eyebrow mb-2">Audit log</p>
        <h1 className="qa-h1">Every change, <span className="qa-italic-accent">traceable.</span></h1>
        <p className="qa-deck mt-2 text-ink-soft">Append-only. The application cannot update or delete these rows.</p>
      </div>

      <div className="qa-card p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label className="qa-label">Action prefix</label>
          <input className="qa-input" placeholder="e.g. client. or auth." value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))} />
        </div>
        <div>
          <label className="qa-label">Entity type</label>
          <input className="qa-input" placeholder="client, file_note, advice_doc…" value={filters.entityType} onChange={e => setFilters(f => ({ ...f, entityType: e.target.value }))} />
        </div>
        <div>
          <label className="qa-label">From</label>
          <input type="date" className="qa-input" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
        </div>
        <div>
          <label className="qa-label">To</label>
          <input type="date" className="qa-input" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
        </div>
      </div>

      <div className="flex justify-end gap-3 mb-3">
        <a className="btn-ghost text-sm" href={`/api/audit/logs?${qs.toString()}&format=csv`}>Export CSV</a>
        <a className="btn-ghost text-sm" href={`/api/audit/logs?${qs.toString()}&format=xlsx`}>Export XLSX</a>
        <button className="btn-ghost text-sm" onClick={() => setFilters({ action: '', entityType: '', from: '', to: '' })}>Reset</button>
      </div>

      {isLoading ? <QASkeleton variant="table" /> : (
        <div className="overflow-x-auto border border-hairline">
          <table className="qa-table">
            <thead>
              <tr><th>Time</th><th>User</th><th>Action</th><th>Entity</th><th>OK</th><th>IP</th></tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="qa-mono text-xs">{new Date(r.occurredAt).toLocaleString('en-AU')}</td>
                  <td>{r.userName ?? r.userEmail ?? '—'}</td>
                  <td className="qa-mono text-xs">{r.action}</td>
                  <td className="qa-mono text-xs">{r.entityType ?? '—'} {r.entityId ? `(${String(r.entityId).slice(0, 8)}…)` : ''}</td>
                  <td>{r.success ? <span className="qa-badge qa-badge--pass">YES</span> : <span className="qa-badge qa-badge--out">NO</span>}</td>
                  <td className="qa-mono text-xs">{r.ip ?? '—'}</td>
                </tr>
              ))}
              {!isLoading && (data?.data?.length ?? 0) === 0 && (
                <tr><td colSpan={6}><QAEmpty inline title="No audit events match the current filters." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
